import React, { useEffect, useState } from "react";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PlayBackground from "../components/PlayBackground";
import { wwHomeColorsLight } from "../lib/theme";

const ASSET = `${process.env.PUBLIC_URL || ""}/prototype/word-wheel`;

/** App background — ad resource 9be42bbc-baa1-4698-8814-3f4b410c2ea7. */
const PACKS_APP_BACKGROUND =
    "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/ad/iphone/background/9be42bbc-baa1-4698-8814-3f4b410c2ea7.jpg";

const PACKS = [
    {
        code: "classic",
        name: "Classic",
        limit: 200,
        color: "#B45309",
        glow: "#F59E0B",
        gradient: "linear-gradient(135deg, #F59E0B 0%, #92400E 100%)",
        icon: `${ASSET}/classic-swords.webp`,
        title: "Classic Challenge",
        price: "$1.99",
        promoTitle: "Easy to Start, Relaxing to Play",
        promoBody:
            "Simple words make solving a breeze. Enjoy a smooth, relaxing pace and reach the finish line quickly!",
    },
    {
        code: "hard_quest",
        name: "Hard Quest",
        limit: 400,
        color: "#BE123C",
        glow: "#FB7185",
        gradient: "linear-gradient(135deg, #F43F5E 0%, #9F1239 100%)",
        icon: `${ASSET}/hard-quest-peak.png`,
        title: "Hard Quest",
        price: "$2.99",
        promoTitle: "A True Test of Skill",
        promoBody:
            "No 3-letter words here! Every puzzle features at least 5 longer words with challenging goals. Think you can handle the pressure?",
    },
    {
        code: "master",
        name: "Master",
        limit: 500,
        color: "#6D28D9",
        glow: "#A78BFA",
        gradient: "linear-gradient(135deg, #8B5CF6 0%, #5B21B6 100%)",
        icon: `${ASSET}/master-scroll.webp`,
        title: "Master Quest",
        price: "$2.99",
        promoTitle: "The Ultimate Journey",
        promoBody:
            "Starts gentle, then builds into serious brain-teasers. A long, rewarding challenge designed for true word masters!",
    },
];

const home = wwHomeColorsLight;

function PackMark({ src, color }) {
    return (
        <Box
            aria-hidden
            sx={{
                width: 40,
                height: 40,
                bgcolor: color,
                WebkitMaskImage: `url("${src}")`,
                maskImage: `url("${src}")`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
                WebkitMaskSize: "contain",
                maskSize: "contain",
            }}
        />
    );
}

/**
 * 1920×1080 capture of the Android home hub (Classic / Hard Quest / Master).
 * Centered 9:16 phone, same pillarbox as the 1080p play recording.
 */
const PACK_POPUP_MS = 3000;

export default function Recording1080pPacksPage() {
    const [packIndex, setPackIndex] = useState(0);
    const openPack = PACKS[packIndex] || null;

    useEffect(() => {
        const timer = window.setInterval(() => {
            setPackIndex((index) => (index + 1) % PACKS.length);
        }, PACK_POPUP_MS);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById("root");
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            bodyMargin: body.style.margin,
            bodyBg: body.style.background,
            rootOverflow: root ? root.style.overflow : "",
        };
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        body.style.margin = "0";
        body.style.background = "#0f172a";
        if (root) root.style.overflow = "hidden";
        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            body.style.margin = prev.bodyMargin;
            body.style.background = prev.bodyBg;
            if (root) root.style.overflow = prev.rootOverflow || "";
        };
    }, []);

    return (
        <Box
            data-ww-capture="1080p-packs"
            sx={{
                position: "fixed",
                inset: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "#0f172a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
            }}
        >
            <Box aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.4 }}>
                <PlayBackground background={PACKS_APP_BACKGROUND} theme="random" />
            </Box>
            <Box
                sx={{
                    height: "100%",
                    aspectRatio: "9 / 16",
                    maxWidth: "100%",
                    position: "relative",
                    zIndex: 1,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    color: home.text,
                    boxSizing: "border-box",
                }}
            >
                <PlayBackground background={PACKS_APP_BACKGROUND} theme="random" showWash={false} />
                <Box sx={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "flex-end", px: 2, pt: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            bgcolor: home.surface,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: home.shadowSm,
                        }}
                    >
                        <SettingsOutlinedIcon sx={{ fontSize: 20, color: home.text }} />
                    </Box>
                </Box>

                <Box sx={{ position: "relative", zIndex: 1, px: 2.25, pt: 0.5, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: 34, fontWeight: 800, letterSpacing: "-0.6px", lineHeight: 1.1, color: home.text }}>
                        Word Wheel Quest
                    </Typography>
                    <Typography
                        sx={{
                            mt: 1.25,
                            fontSize: 15,
                            color: home.text,
                            textShadow: "0 1px 2px rgba(255,255,255,0.9)",
                        }}
                    >
                        Continue your journey
                    </Typography>

                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", py: 1.5 }}>
                        <Box sx={{ position: "relative" }}>
                            <Box
                                sx={{
                                    borderRadius: "28px",
                                    border: "1px solid #CDB069",
                                    bgcolor: "#E3C17D",
                                    color: "#3A2A1A",
                                    p: 2.5,
                                    boxShadow: "0 6px 14px rgba(107, 78, 31, 0.18)",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
                                    <Box
                                        component="img"
                                        src={`${ASSET}/home-wheel-art.webp`}
                                        alt=""
                                        sx={{ width: 92, height: 92, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                px: 1.25,
                                                py: 0.25,
                                                mb: 0.75,
                                                borderRadius: 999,
                                                bgcolor: "rgba(58, 42, 26, 0.12)",
                                            }}
                                        >
                                            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "1.1px", color: "#3A2A1A" }}>
                                                LEVEL
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.4px", lineHeight: 1.1 }}>
                                            Level 1
                                        </Typography>
                                        <Box sx={{ mt: 1.25, height: 4, borderRadius: 999, bgcolor: "rgba(58, 42, 26, 0.18)", overflow: "hidden" }}>
                                            <Box sx={{ width: "8%", height: "100%", bgcolor: "#8B6914" }} />
                                        </Box>
                                        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <SearchIcon sx={{ fontSize: 14, color: "rgba(58, 42, 26, 0.72)" }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "rgba(58, 42, 26, 0.72)" }}>
                                                0 / 3 words found
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        mt: 2.25,
                                        minHeight: 52,
                                        borderRadius: 999,
                                        bgcolor: "#F8EED8",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                    }}
                                >
                                    <PlayArrowIcon sx={{ fontSize: 20, color: "#3A2A1A" }} />
                                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#3A2A1A" }}>
                                        Continue
                                    </Typography>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: -16,
                                    right: -6,
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    bgcolor: "#F5C542",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CardGiftcardIcon sx={{ fontSize: 20, color: "#B45309" }} />
                            </Box>
                        </Box>

                        <Typography
                            sx={{
                                alignSelf: "center",
                                mt: 1.75,
                                fontSize: 15,
                                fontWeight: 700,
                                color: home.primary,
                                textDecoration: "underline",
                            }}
                        >
                            Tutorial
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.25, mb: 1.25 }}>
                        <HubTile
                            iconBg="rgba(61, 155, 116, 0.14)"
                            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: home.primaryGlow }} />}
                            title="Daily Puzzle"
                            subtitle="New puzzle every day"
                        />
                        <HubTile
                            iconBg="rgba(234, 179, 148, 0.35)"
                            icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 18, color: home.text }} />}
                            title="Shop"
                            subtitle="Coins, bundles and more"
                            trailing={<ChevronRightIcon sx={{ fontSize: 16, color: home.textMuted }} />}
                        />
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                        {PACKS.map((pack) => (
                            <Box
                                key={pack.code}
                                component="button"
                                type="button"
                                aria-label={pack.name}
                                onClick={() => setPackIndex(PACKS.findIndex((item) => item.code === pack.code))}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    borderRadius: "16px",
                                    border: `2px solid ${pack.glow}`,
                                    bgcolor: home.surface,
                                    px: 0.75,
                                    py: 1.25,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    font: "inherit",
                                    color: "inherit",
                                }}
                            >
                                <PackMark src={pack.icon} color={pack.color} />
                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontSize: 12,
                                        lineHeight: 1.25,
                                        fontWeight: 800,
                                        color: pack.color,
                                        textAlign: "center",
                                    }}
                                >
                                    {pack.name}
                                </Typography>
                                <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <LockOutlinedIcon sx={{ fontSize: 12, color: pack.color }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: pack.color }}>
                                        {pack.limit}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            mb: 1.5,
                            borderRadius: "18px",
                            border: `1px solid ${home.border}`,
                            bgcolor: home.surface,
                            p: 1.75,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                bgcolor: "rgba(61, 155, 116, 0.14)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <CloudOutlinedIcon sx={{ fontSize: 22, color: home.primaryGlow }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: home.text }}>
                                Guest Mode
                            </Typography>
                            <Typography sx={{ mt: 0.25, fontSize: 12, lineHeight: 1.4, color: home.textMuted }}>
                                Sign in to sync your progress and play on any device
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                border: "1.5px solid #1b4d3e",
                                borderRadius: "12px",
                                px: 1.5,
                                py: 1,
                                flexShrink: 0,
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#1b4d3e" }}>
                                Sign In
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {openPack ? (
                    <PackPurchasePopup pack={openPack} onClose={() => {}} />
                ) : null}
            </Box>
        </Box>
    );
}

function PackPurchasePopup({ pack, onClose }) {
    return (
        <Box
            role="presentation"
            onClick={onClose}
            data-ww-pack-popup={pack.code}
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 5,
                bgcolor: "rgba(4, 24, 28, 0.72)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 3.5,
            }}
        >
            <Box
                role="dialog"
                aria-label={pack.title}
                onClick={(event) => event.stopPropagation()}
                sx={{
                    width: "100%",
                    maxWidth: 340,
                    borderRadius: "22px",
                    border: `1.5px solid ${pack.color}`,
                    bgcolor: home.surface,
                    px: 2.75,
                    pt: 3,
                    pb: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <PackMark src={pack.icon} color={pack.color} />
                <Typography sx={{ mt: 1.5, fontSize: 20, fontWeight: 800, textAlign: "center", color: home.text }}>
                    {pack.title}
                </Typography>
                <Typography sx={{ mt: 0.75, fontSize: 15, lineHeight: 1.35, fontWeight: 800, textAlign: "center", color: pack.color }}>
                    {pack.promoTitle}
                </Typography>
                <Typography sx={{ mt: 1.25, fontSize: 15, lineHeight: 1.45, textAlign: "center", color: home.textMuted }}>
                    {pack.promoBody}
                </Typography>
                <Box
                    sx={{
                        mt: 2.5,
                        alignSelf: "stretch",
                        minHeight: 48,
                        borderRadius: "14px",
                        background: pack.gradient,
                        boxShadow: `0 0 16px ${pack.glow}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography sx={{ color: "#fff", fontSize: 16, fontWeight: 800 }}>{pack.price}</Typography>
                </Box>
                <Box
                    component="button"
                    type="button"
                    onClick={onClose}
                    sx={{
                        mt: 1.5,
                        py: 1,
                        border: 0,
                        bgcolor: "transparent",
                        cursor: "pointer",
                        font: "inherit",
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: home.textMuted }}>Not now</Typography>
                </Box>
            </Box>
        </Box>
    );
}

function HubTile({ icon, iconBg, title, subtitle, trailing }) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: "16px",
                border: `1px solid ${home.border}`,
                bgcolor: home.surface,
                px: 1.5,
                py: 1.5,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box
                    sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        bgcolor: iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 800, color: home.text, lineHeight: 1.2 }}>
                    {title}
                </Typography>
                {trailing}
            </Box>
            <Typography sx={{ fontSize: 12, lineHeight: 1.35, color: home.textMuted }}>
                {subtitle}
            </Typography>
        </Box>
    );
}
